import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClientApiError, apiRequest } from "../auth/api";
import { Badge, EmptyState, ErrorState, Skeleton } from "../components/ui";

interface AuthorityDog {
  id: string;
  slug: string;
  registeredName: string;
  workflowStatus: string;
  version: number;
  updatedAt: string;
  relationTypes: string[];
  scopes: string[];
  sourceTypes: string[];
}

interface AuthorityDogDirectory {
  items: AuthorityDog[];
  total: number;
}

const formatter = new Intl.DateTimeFormat("vi-VN", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Asia/Ho_Chi_Minh",
});

export function OwnershipDogIndexPage() {
  const [data, setData] = useState<AuthorityDogDirectory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await apiRequest<AuthorityDogDirectory>("/api/v1/ownership/my-dogs"));
      setError(null);
    } catch (caught) {
      if (caught instanceof ClientApiError) {
        setError({ message: caught.problem.message, requestId: caught.problem.requestId });
      } else {
        setError({ message: "Không thể tải danh sách chó bạn đang có quyền quản lý." });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="ownership-page" data-testid="ownership-dog-index-page">
      <header className="page-heading authority-heading">
        <div>
          <p className="eyebrow">Current authority</p>
          <h2>Chó tôi có quyền quản lý</h2>
          <p>Danh sách dựa trên owner, co-owner, breeder, handler hoặc editor đang hiệu lực.</p>
        </div>
        <Link className="ui-button ui-button--secondary ui-button--md" to="/member/ownership">
          Xem lời mời & yêu cầu
        </Link>
      </header>

      {loading ? <Skeleton lines={8} /> : null}
      {!loading && error ? (
        <ErrorState
          title="Không tải được danh sách quyền"
          description={error.message}
          requestId={error.requestId}
          action={{ label: "Tải lại", onClick: () => void load() }}
        />
      ) : null}
      {!loading && !error && data?.items.length === 0 ? (
        <div className="authority-empty-state">
          <EmptyState
            title="Bạn chưa có quyền hiện hành trên hồ sơ chó nào"
            description="Mở một hồ sơ chó công khai để gửi claim có bằng chứng, hoặc chấp nhận lời mời editor/transfer."
          />
          <Link className="ui-button ui-button--primary ui-button--md" to="/dogs">
            Xem hồ sơ chó
          </Link>
        </div>
      ) : null}
      {!loading && !error && data && data.items.length > 0 ? (
        <div className="authority-dog-directory">
          {data.items.map((dog) => (
            <article key={dog.id} className="authority-dog-directory__item">
              <div className="authority-dog-directory__identity" aria-hidden="true">
                {dog.registeredName.slice(0, 1).toUpperCase()}
              </div>
              <div className="authority-dog-directory__body">
                <header>
                  <div>
                    <h3>{dog.registeredName}</h3>
                    <p>{dog.relationTypes.join(" · ")} · version {dog.version}</p>
                  </div>
                  <Badge tone={dog.workflowStatus === "disputed" ? "danger" : "success"}>
                    {dog.workflowStatus}
                  </Badge>
                </header>
                <p className="scope-line">{dog.scopes.join(" · ")}</p>
                <p>Cập nhật {formatter.format(new Date(dog.updatedAt))}</p>
                <div className="button-row">
                  <Link
                    className="ui-button ui-button--primary ui-button--sm"
                    to={`/member/dogs/${dog.id}/ownership`}
                  >
                    Quản lý quyền
                  </Link>
                  <Link className="ui-button ui-button--ghost ui-button--sm" to={`/dogs/${dog.slug}`}>
                    Hồ sơ công khai
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}
